import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ===== Types =====
export interface DashboardStats {
  personal: {
    ownedProfiles: number;
    createdProfiles: number;
    totalRoyaltiesEarned: number;
    royalties30d: number;
    buys24h: number;
    sells24h: number;
    klyxdToday: number;
  };
  social: {
    klyqueCount: number;
    klicksCount: number;
  };
  area: {
    totalUsersInArea: number;
    userRank: number;
    hasLocation: boolean;
  };
  recentActivity: Array<{
    id: string;
    profileId: string;
    price: string;
    tradeType: string;
    fromUsername: string;
    toUsername: string;
    createdAt: string;
  }>;
}

export interface Profile {
  id: string;
  profileId: string;
  priceKlv: string;
  metadataUri: string;
  isListed?: boolean;
  creator: {
    username: string;
    displayName: string;
    avatar: string;
  };
  owner?: {
    username: string;
    displayName: string;
    avatar: string;
  };
  tradeCount?: number;
  favoriteCount?: number;
  trades24h?: number;
  /** Set on profiles surfaced in the Spotlighted carousel so the card
   *  can render a countdown. ISO timestamp from spotlight_promotion.end_time. */
  spotlightEndTime?: string;
}

export interface Post {
  id: string;
  body: string;
  mediaUrl?: string;
  createdAt: string;
  /** Server-set timestamp of the last edit — bumped by
   *  PostController.updatePost (#292). Client uses updatedAt >
   *  createdAt (with a small tolerance) to render an "(edited)"
   *  indicator on the post card. Optional — pre-#292 rows return
   *  the same value as createdAt so the indicator hides. */
  updatedAt?: string;
  postedBy: {
    userId: string;
    username: string;
    displayName: string;
    avatar: string;
  };
  ownerUserId?: string; // Profile owner (may differ from poster)
  stats: {
    likes: number;
    dislikes: number;
    comments: number;
  };
  userInteraction?: {
    liked: boolean;
    disliked: boolean;
  };
  spotlight?: {
    startTime: string;
    endTime: string;
    cost: string;
    token: string;
  };
  location?: string;
  // Paywall fields
  viewType?: 'public' | 'paid' | 'ppv';
  ppvPrice?: string | null;
  ppvTokenId?: string | null;
  isLocked?: boolean;
  previewText?: string | null;
  hasMedia?: boolean;
  /** PPV preview mode (#257). Drives what an unpaid viewer sees:
   *   - 'none'        full paywall, body + media stripped
   *   - 'char_limit'  first N chars of body in previewText
   *   - 'text_only'   full body visible, only media is paywalled */
  previewMode?: 'none' | 'char_limit' | 'text_only' | null;
  /** Only consulted when previewMode === 'char_limit'. Server clamps to (0, 1000]. */
  previewCharLimit?: number | null;
  /** Creator-declared AI generation flag (#531). true = creator ticked
   *  the AI-generated checkbox at post-creation. Drives a subtle badge
   *  on the post card. */
  isAiGenerated?: boolean;
  /** Creator's on-chain profile ID — needed by the paywall modal to build payment transactions */
  profileId?: number | null;
  /** One-time view fee in display units, e.g. "5.00" (fetched lazily when paywall opens) */
  viewFee?: string | null;
  /** Subscription fee in display units (fetched lazily when paywall opens) */
  subscriptionFee?: string | null;
  /** Subscription period in seconds (fetched lazily when paywall opens) */
  subscriptionPeriodSeconds?: number | null;
}

export interface UserProfile {
  userId: string;
  username: string;
  displayName: string;
  avatar: string;
  bio?: string;
  headline?: string;
  followedAt?: string;
  isFollowingBack?: boolean;
  stats?: {
    followers: number;
    following: number;
    profiles: number;
  };
}

export interface FavoriteStats {
  received: {
    today: number;
    thisWeek: number;
    allTime: number;
  };
  given: number;
}

export interface FollowStats {
  klyqueCount: number;
  klicksCount: number;
  mutualCount: number;
  isFollowing?: boolean;
}

interface DashboardState {
  // Stats
  stats: DashboardStats | null;
  topProfiles: Profile[];
  trendingProfiles: Profile[];
  activeSpotlightedProfiles: Profile[];

  // Feed
  spotlightFeed: Post[];
  locationFeed: Post[];
  klyqueFeed: Post[];
  activeFeedTab: 'spotlight' | 'location' | 'klyque';
  feedHasMore: boolean;
  feedOffset: number;
  locationRadius: number;
  locationUnit: 'miles' | 'km';
  userLocationName: string | null;

  // Favorites (Klyx'd)
  myFavorites: Profile[];
  favoriteStats: FavoriteStats | null;

  // Follow (Klyque/Klicks)
  klyque: UserProfile[];
  klicks: UserProfile[];
  followStats: FollowStats | null;
  suggestedFollows: UserProfile[];

  // UI state
  isLoading: boolean;
  error: string | null;
  statsLoading: boolean;
  feedLoading: boolean;
  followLoading: boolean;
}

const initialState: DashboardState = {
  stats: null,
  topProfiles: [],
  trendingProfiles: [],
  activeSpotlightedProfiles: [],

  spotlightFeed: [],
  locationFeed: [],
  klyqueFeed: [],
  activeFeedTab: 'location',
  feedHasMore: true,
  feedOffset: 0,
  locationRadius: 250,
  locationUnit: 'miles',
  userLocationName: null,

  myFavorites: [],
  favoriteStats: null,

  klyque: [],
  klicks: [],
  followStats: null,
  suggestedFollows: [],

  isLoading: false,
  error: null,
  statsLoading: false,
  feedLoading: false,
  followLoading: false,
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    // Stats actions
    setDashboardStats: (state, action: PayloadAction<DashboardStats>) => {
      state.stats = action.payload;
      state.statsLoading = false;
    },

    setTopProfiles: (state, action: PayloadAction<Profile[]>) => {
      state.topProfiles = action.payload;
    },

    setTrendingProfiles: (state, action: PayloadAction<Profile[]>) => {
      state.trendingProfiles = action.payload;
    },

    setActiveSpotlightedProfiles: (state, action: PayloadAction<Profile[]>) => {
      state.activeSpotlightedProfiles = action.payload;
    },

    // Feed actions
    setActiveFeedTab: (state, action: PayloadAction<'spotlight' | 'location' | 'klyque'>) => {
      state.activeFeedTab = action.payload;
      state.feedOffset = 0;
      state.feedLoading = false; // Reset loading state when switching tabs
    },

    setSpotlightFeed: (state, action: PayloadAction<{ posts: Post[]; hasMore: boolean; append?: boolean }>) => {
      if (action.payload.append) {
        state.spotlightFeed = [...state.spotlightFeed, ...action.payload.posts];
      } else {
        state.spotlightFeed = action.payload.posts;
      }
      state.feedHasMore = action.payload.hasMore;
      state.feedLoading = false;
    },

    setLocationFeed: (state, action: PayloadAction<{ posts: Post[]; hasMore: boolean; append?: boolean; userLocation?: { name: string; radius: number; unit: string } }>) => {
      if (action.payload.append) {
        state.locationFeed = [...state.locationFeed, ...action.payload.posts];
      } else {
        state.locationFeed = action.payload.posts;
      }
      state.feedHasMore = action.payload.hasMore;
      state.feedLoading = false;
      if (action.payload.userLocation) {
        state.userLocationName = action.payload.userLocation.name;
      }
    },

    setKlyqueFeed: (state, action: PayloadAction<{ posts: Post[]; hasMore: boolean; append?: boolean }>) => {
      if (action.payload.append) {
        state.klyqueFeed = [...state.klyqueFeed, ...action.payload.posts];
      } else {
        state.klyqueFeed = action.payload.posts;
      }
      state.feedHasMore = action.payload.hasMore;
      state.feedLoading = false;
    },

    incrementFeedOffset: (state) => {
      state.feedOffset += 20;
    },

    resetFeedOffset: (state) => {
      state.feedOffset = 0;
    },

    setLocationRadius: (state, action: PayloadAction<number>) => {
      state.locationRadius = action.payload;
      state.feedOffset = 0;
    },

    setLocationUnit: (state, action: PayloadAction<'miles' | 'km'>) => {
      state.locationUnit = action.payload;
      state.feedOffset = 0;
    },

    updatePostInteraction: (state, action: PayloadAction<{ postId: string; liked?: boolean; disliked?: boolean }>) => {
      const updatePost = (post: Post) => {
        if (post.id === action.payload.postId && post.userInteraction) {
          // Store old values to detect actual changes
          const oldLiked = post.userInteraction.liked;
          const oldDisliked = post.userInteraction.disliked;
          const newLiked = action.payload.liked !== undefined ? action.payload.liked : oldLiked;
          const newDisliked = action.payload.disliked !== undefined ? action.payload.disliked : oldDisliked;

          // Calculate the like count delta
          let likeDelta = 0;
          if (oldLiked && !newLiked) {
            likeDelta = -1; // Removing a like
          } else if (!oldLiked && newLiked) {
            likeDelta = 1; // Adding a like
          }

          // Calculate the dislike count delta
          let dislikeDelta = 0;
          if (oldDisliked && !newDisliked) {
            dislikeDelta = -1; // Removing a dislike
          } else if (!oldDisliked && newDisliked) {
            dislikeDelta = 1; // Adding a dislike
          }

          // Apply the changes
          post.stats.likes += likeDelta;
          post.stats.dislikes += dislikeDelta;
          post.userInteraction.liked = newLiked;
          post.userInteraction.disliked = newDisliked;
        }
      };

      state.spotlightFeed.forEach(updatePost);
      state.locationFeed.forEach(updatePost);
      state.klyqueFeed.forEach(updatePost);
    },

    updatePostCommentCount: (state, action: PayloadAction<{ postId: string; increment: number }>) => {
      const updatePost = (post: Post) => {
        if (post.id === action.payload.postId) {
          post.stats.comments += action.payload.increment;
        }
      };

      state.spotlightFeed.forEach(updatePost);
      state.locationFeed.forEach(updatePost);
      state.klyqueFeed.forEach(updatePost);
    },

    updatePostSpotlight: (state, action: PayloadAction<{ postId: string; durationHours: number }>) => {
      const { postId, durationHours } = action.payload;
      const updatePost = (post: Post) => {
        if (post.id === postId) {
          const now = new Date();
          const endTime = new Date(now.getTime() + durationHours * 60 * 60 * 1000);
          post.spotlight = {
            startTime: now.toISOString(),
            endTime: endTime.toISOString(),
            cost: '0', // We don't have the exact cost here, but it's not critical for display
            token: 'KLYX',
          };
        }
      };

      state.spotlightFeed.forEach(updatePost);
      state.locationFeed.forEach(updatePost);
      state.klyqueFeed.forEach(updatePost);
    },

    // Post edit — apply body / mediaUrl / preview / AI-flag changes
    // to every feed slice that might hold the post, so the card
    // re-renders without a refetch (#292).
    updatePostContent: (state, action: PayloadAction<{
      postId: string;
      body?: string;
      mediaUrl?: string | null;
      previewMode?: 'none' | 'char_limit' | 'text_only' | null;
      previewCharLimit?: number | null;
      isAiGenerated?: boolean;
      updatedAt?: string;
    }>) => {
      const { postId, body, mediaUrl, previewMode, previewCharLimit, isAiGenerated, updatedAt } = action.payload;
      const nextUpdatedAt = updatedAt ?? new Date().toISOString();
      const applyEdit = (post: Post) => {
        if (post.id !== postId) return;
        if (body !== undefined) post.body = body;
        if (mediaUrl !== undefined) post.mediaUrl = mediaUrl ?? '';
        if (previewMode !== undefined) post.previewMode = previewMode;
        if (previewCharLimit !== undefined) post.previewCharLimit = previewCharLimit;
        if (isAiGenerated !== undefined) post.isAiGenerated = isAiGenerated;
        // Bump updatedAt so the "(edited)" indicator lights up
        // immediately without waiting for a refetch.
        post.updatedAt = nextUpdatedAt;
      };
      state.spotlightFeed.forEach(applyEdit);
      state.locationFeed.forEach(applyEdit);
      state.klyqueFeed.forEach(applyEdit);
    },

    // Favorites actions
    setMyFavorites: (state, action: PayloadAction<Profile[]>) => {
      state.myFavorites = action.payload;
    },

    setFavoriteStats: (state, action: PayloadAction<FavoriteStats>) => {
      state.favoriteStats = action.payload;
    },

    toggleProfileFavorite: (state, action: PayloadAction<{ profileId: string; favorited: boolean }>) => {
      const profile = [...state.topProfiles, ...state.trendingProfiles].find(p => p.id === action.payload.profileId);
      if (profile) {
        if (action.payload.favorited) {
          profile.favoriteCount = (profile.favoriteCount || 0) + 1;
        } else {
          profile.favoriteCount = Math.max((profile.favoriteCount || 0) - 1, 0);
        }
      }
    },

    // Follow actions
    setKlyque: (state, action: PayloadAction<UserProfile[]>) => {
      state.klyque = action.payload;
      state.followLoading = false;
    },

    setKlicks: (state, action: PayloadAction<UserProfile[]>) => {
      state.klicks = action.payload;
      state.followLoading = false;
    },

    setFollowStats: (state, action: PayloadAction<FollowStats>) => {
      state.followStats = action.payload;
    },

    setSuggestedFollows: (state, action: PayloadAction<UserProfile[]>) => {
      state.suggestedFollows = action.payload;
    },

    updateFollowStatus: (state, action: PayloadAction<{ userId: string; following: boolean; user?: UserProfile }>) => {
      // Update in suggested follows
      const suggested = state.suggestedFollows.find(u => u.userId === action.payload.userId);
      if (suggested && action.payload.following) {
        state.suggestedFollows = state.suggestedFollows.filter(u => u.userId !== action.payload.userId);
      }

      // Update Klyque list
      if (action.payload.following) {
        // Add to klyque if not already there
        const alreadyInKlyque = state.klyque.some(u => u.userId === action.payload.userId);
        if (!alreadyInKlyque && action.payload.user) {
          state.klyque.push(action.payload.user);
        }
      } else {
        // Remove from klyque
        state.klyque = state.klyque.filter(u => u.userId !== action.payload.userId);
      }

      // Update follow stats
      if (state.followStats) {
        if (action.payload.following) {
          state.followStats.klyqueCount += 1;
        } else {
          state.followStats.klyqueCount = Math.max(state.followStats.klyqueCount - 1, 0);
        }
      }
    },

    // Loading and error actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setStatsLoading: (state, action: PayloadAction<boolean>) => {
      state.statsLoading = action.payload;
    },

    setFeedLoading: (state, action: PayloadAction<boolean>) => {
      state.feedLoading = action.payload;
    },

    setFollowLoading: (state, action: PayloadAction<boolean>) => {
      state.followLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
      state.statsLoading = false;
      state.feedLoading = false;
      state.followLoading = false;
    },

    clearError: (state) => {
      state.error = null;
    },

    resetDashboard: () => initialState,
  },
});

export const {
  setDashboardStats,
  setTopProfiles,
  setTrendingProfiles,
  setActiveSpotlightedProfiles,
  setActiveFeedTab,
  setSpotlightFeed,
  setLocationFeed,
  setKlyqueFeed,
  incrementFeedOffset,
  resetFeedOffset,
  setLocationRadius,
  setLocationUnit,
  updatePostInteraction,
  updatePostCommentCount,
  updatePostSpotlight,
  updatePostContent,
  setMyFavorites,
  setFavoriteStats,
  toggleProfileFavorite,
  setKlyque,
  setKlicks,
  setFollowStats,
  setSuggestedFollows,
  updateFollowStatus,
  setLoading,
  setStatsLoading,
  setFeedLoading,
  setFollowLoading,
  setError,
  clearError,
  resetDashboard,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
